import type { DroneWeather, FlightCategory } from 'hooks/useAirspaceWeather/types';

const MINUTES_PER_HOUR = 60;
const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const ROUND_FIVE_DEG = 5;

const CARDINAL_DIRECTIONS = [
    'N',
    'NNE',
    'NE',
    'ENE',
    'E',
    'ESE',
    'SE',
    'SSE',
    'S',
    'SSW',
    'SW',
    'WSW',
    'W',
    'WNW',
    'NW',
    'NNW'
];
const DIRECTIONS_PER_SLICE = 360 / CARDINAL_DIRECTIONS.length;

export const formatWind = (weather: DroneWeather): string => {
    const speed = weather.windSpeedKt;

    if (speed === null || speed === 0) {
        return 'Calm';
    }

    const direction = weather.windDirection;
    const directionText =
        typeof direction === 'number'
            ? `${Math.round(direction / ROUND_FIVE_DEG) * ROUND_FIVE_DEG}°`
            : typeof direction === 'string' && direction !== ''
              ? direction
              : '';
    const gustText = weather.windGustKt !== null ? `G${weather.windGustKt}` : '';

    return directionText.length > 0
        ? `${directionText} @ ${speed}${gustText} kt`
        : `${speed}${gustText} kt`;
};

export const formatVisibility = (weather: DroneWeather): string | null =>
    weather.visibilitySm === null ? null : `${weather.visibilitySm} sm`;

export const formatCeiling = (weather: DroneWeather): string =>
    weather.ceilingFt === null ? 'Clear' : `${weather.ceilingFt.toLocaleString('en-US')} ft`;

export const formatObservationAge = (observationTime: string | null): string | null => {
    if (observationTime === null) {
        return null;
    }

    const parsed = Date.parse(observationTime);

    if (!Number.isFinite(parsed)) {
        return null;
    }

    const ageMs = Date.now() - parsed;

    if (ageMs < 0) {
        return 'just now';
    }

    const ageMinutes = Math.floor(ageMs / (MS_PER_SECOND * SECONDS_PER_MINUTE));

    if (ageMinutes < 1) {
        return 'just now';
    }

    if (ageMinutes < MINUTES_PER_HOUR) {
        return `${ageMinutes}m ago`;
    }

    const ageHours = Math.floor(ageMinutes / MINUTES_PER_HOUR);

    return `${ageHours}h ago`;
};

export const formatCardinalBearing = (direction: number | string | null): string | null => {
    if (typeof direction !== 'number' || !Number.isFinite(direction)) {
        return null;
    }

    const normalized = ((direction % 360) + 360) % 360;
    const index = Math.round(normalized / DIRECTIONS_PER_SLICE) % CARDINAL_DIRECTIONS.length;

    return CARDINAL_DIRECTIONS[index];
};

export const flightCategoryDescription: Record<FlightCategory, string> = {
    IFR: 'Instrument conditions — drone ops not recommended',
    LIFR: 'Low-IFR — minimum visibility, ceilings below 500 ft',
    MVFR: 'Marginal VFR — reduced ceilings or visibility',
    UNKNOWN: 'Flight category unavailable from observation',
    VFR: 'Visual flight rules — clear conditions'
};

const VFR_CEILING_FT = 3000;
const VFR_VISIBILITY_SM = 5;
const MVFR_CEILING_FT = 1000;
const MVFR_VISIBILITY_SM = 3;
const IFR_CEILING_FT = 500;
const IFR_VISIBILITY_SM = 1;

// Fallback when the upstream METAR doesn't include a flight-category tag (common for AUTO
// stations). Derived from FAA standard thresholds on ceiling + visibility. Used only when
// the backend returns UNKNOWN but we have usable observation data.
export const resolveFlightCategory = (weather: DroneWeather): FlightCategory => {
    if (weather.flightCategory !== 'UNKNOWN') {
        return weather.flightCategory;
    }

    const ceiling = weather.ceilingFt;
    const visibility = weather.visibilitySm;

    if (ceiling === null && visibility === null) {
        return 'UNKNOWN';
    }

    const effectiveCeiling = ceiling ?? Number.POSITIVE_INFINITY;
    const effectiveVisibility = visibility ?? Number.POSITIVE_INFINITY;

    if (effectiveCeiling < IFR_CEILING_FT || effectiveVisibility < IFR_VISIBILITY_SM) {
        return 'LIFR';
    }

    if (effectiveCeiling < MVFR_CEILING_FT || effectiveVisibility < MVFR_VISIBILITY_SM) {
        return 'IFR';
    }

    if (effectiveCeiling < VFR_CEILING_FT || effectiveVisibility < VFR_VISIBILITY_SM) {
        return 'MVFR';
    }

    return 'VFR';
};
